import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Redux action for sorting
import { handleSorting } from 'store/Admin/OrderList';

// Hooks & utils
import { useDeadlineFilter } from 'utils/hooks/useDeadlineFilter';
import { usePagination } from 'utils/hooks/usePagination';
import { useHistoryState } from 'utils/hooks/useHistoryState';
import { formatDate } from 'utils/DateUtils';
import {
  ORDERSTATUS,
  FILTER_ORDER_TYPE_LIST,
  ORDER_TYPE,
  pageNbOptions,
} from 'utils/Constants';
import { filterOrders } from 'helpers/FilterOrders';

// UI Components
import { Paginator } from 'components/shared/Paginator';
import { Loader } from 'components/shared/Loader';
import ErrorComponent from 'components/shared/ErrorComponent';
import { ProgressBar } from 'components/shared/ProgressBar';
import { CircularBar } from 'components/shared/CircularBar';
import { CustomDropdownReadOnly } from 'components/shared/DropDown/CustomDropdown';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import DeadlineFilterPopover from 'components/shared/Filter/DeadlineFilterPopover';
import SearchIcon from 'icons/SearchIcon';
import { Button } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'icons/ChevronIcon';
import DataTable from 'components/shared/DataTable';

/**
 * Returns true if we should show the "Type" column
 * (i.e. for all statuses except SMART and SMART_WITH_REVIEW)
 */
const shouldIncludeType = (status) =>
  ![ORDERSTATUS.SMART, ORDERSTATUS.SMART_WITH_REVIEW].includes(status);

/**
 * Returns true if we should show the "Temps restant" column
 * (for INPROGRESS, SMART, SMART_WITH_REVIEW)
 */
const shouldIncludeTempsRestant = (status) =>
  [
    ORDERSTATUS.INPROGRESS,
    ORDERSTATUS.SMART,
    ORDERSTATUS.SMART_WITH_REVIEW,
  ].includes(status);

/**
 * Builds the common/shared columns (title, deadline, writers, progress bars)
 */
const getCommonColumns = ({
  t,
  selectedYear,
  setSelectedYear,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedType,
  setSelectedType,
  handleTypeClick,
  handleTypeClose,
  anchorEl,
  handleWritersClick,
  handleWritersClose,
}) => [
  {
    header: t('Titre de la commande'),
    accessor: 'title',
    sortable: true,
    width: '35%',
  },
  {
    header: t('Deadline'),
    accessor: 'deadline',
    sortable: true,
    width: '15%',
    filter: (
      <DeadlineFilterPopover
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
    ),
  },
  {
    header: t('Rédacteurs'),
    accessor: 'writers',
    sortable: false,
    renderCell: (order, idx) =>
      order.writers.length === 0 ? (
        <Button disabled>{t('0 rédacteurs')}</Button>
      ) : (
        <>
          <Button onClick={(e) => handleWritersClick(idx, e)}>
            <img src="/Images/icon-users-down.png" alt="writers" />
          </Button>
          <CustomDropdownReadOnly
            handleClose={handleWritersClose}
            anchorEl={anchorEl?.[idx]}
            list={order.writers}
            getOptionLabel={(opt) => opt.mail}
          />
        </>
      ),
  },
  {
    header: t('Textes livrés'),
    accessor: 'deliveredTextsAmount',
    sortable: true,
    renderCell: (o) => (
      <ProgressBar amount={o.deliveredTextsAmount} total={o.textAmount} />
    ),
  },
  {
    header: t('Textes validés'),
    accessor: 'validatedTextsAmount',
    sortable: true,
    renderCell: (o) => (
      <ProgressBar amount={o.validatedTextsAmount} total={o.textAmount} />
    ),
  },
  {
    header: t('Textes en ligne'),
    accessor: 'deployedTextsAmount',
    sortable: true,
    renderCell: (o) => (
      <ProgressBar amount={o.deployedTextsAmount} total={o.textAmount} />
    ),
  },
];

export default function OrdersTable({
  title,
  orderStatus,
  orders,
  isLoading,
  isError,
  fetchDisplayData, // function(id, type) => fetch and set in store
  onRowClickRedirectPath, // string path to navigate to on row click
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  // Local state for filters, sorting, pagination
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [selectedType, setSelectedType] = useHistoryState(
    'selectedFilter',
    FILTER_ORDER_TYPE_LIST[0]
  );
  const [titleSearch, setTitleSearch] = useHistoryState('titleSearch', '');
  const [anchorEl, setAnchorEl] = useState(null);
  const [titleFilteredOrders, setTitleFilteredOrders] = useState([]);
  const [sortField, setSortField] = useState('deadline');
  const [sortOrder, setSortOrder] = useState('desc');

  // Reset any route-based logic on mount
  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
  }, []);

  // 1) Filter by order type & search text
  useEffect(() => {
    if (!orders) return;
    const filtered = filterOrders(orders, selectedType, titleSearch, true);
    if (titleSearch.length > 0) setCurrentPage(1);
    setTitleFilteredOrders(filtered);
  }, [orders, selectedType, titleSearch]);

  // 2) Deadline filtering (year / date range)
  const {
    filteredOrders,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDeadlineFilter(titleFilteredOrders);

  // 3) Pagination for filtered orders
  const { currentPage, setCurrentPage, totalPages, paginatedItems } =
    usePagination(filteredOrders, ordersPerPage);

  // 4) Prepare rows, injecting display fields
  const rows = paginatedItems.map((order) => ({
    ...order,
    title: order.title,
    typeLabel: ORDER_TYPE.find((o) => o.type === order.type)?.name,
    deadline: order.deadline ? formatDate(order.deadline) : null,
  }));

  // 5) Dropdown & row-click handlers
  const handleTypeClick = (idx, e) => setAnchorEl({ [idx]: e.currentTarget });
  const handleTypeClose = () => setAnchorEl(null);
  const handleWritersClick = handleTypeClick;
  const handleWritersClose = handleTypeClose;

  const handleRowClick = (order) => {
    fetchDisplayData(order.id, order.type);
    history.push(onRowClickRedirectPath);
  };

  // 6) Build toolbar (title + search + page-size)
  const toolbar = (
    <div className="box-info-header">
      <div className="b-child-1">
        <h1>{title}</h1>
      </div>
      <div className="b-child-2 d-flex justify-content-end align-items-center">
        <div className="box-input-text pe-0">
          <div className="input-group input-cs w-100 pe-0">
            <div className="input-group-text">
              <SearchIcon />
            </div>
            <input
              type="text"
              className="form-control ps-0"
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              placeholder={t('Rechercher')}
            />
          </div>
        </div>
        <span className="me-2">{t('Afficher les résultats')}</span>
        <div className="dropdown" style={{ width: '70px' }}>
          <button
            className="btn dropdown-toggle dropdown-custom w-100"
            type="button"
            id="dropdownMenuButton1"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {ordersPerPage}
            <ChevronDownIcon />
          </button>
          <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
            {pageNbOptions
              .filter((nb) => nb !== ordersPerPage)
              .map((item) => (
                <li key={item}>
                  <button
                    className="dropdown-item"
                    onClick={() => setOrdersPerPage(item)}
                  >
                    {item}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );

  // 7) Assemble columns: common + conditional Type + conditional Temps restant
  const commonColumns = getCommonColumns({
    t,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedType,
    setSelectedType,
    handleTypeClick,
    handleTypeClose,
    anchorEl,
    handleWritersClick,
    handleWritersClose,
  });

  const typeColumn = {
    header: t('Type'),
    accessor: 'typeLabel',
    sortable: false,
    width: '10%',
    filter: (
      <OrderTypeFilter
        orderTypeList={FILTER_ORDER_TYPE_LIST}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        handleClick={handleTypeClick}
        handleClose={handleTypeClose}
        anchorEl={anchorEl}
      />
    ),
  };

  const tempsRestantColumn = {
    header: t('Temps restant'),
    accessor: 'finishedAt',
    sortable: true,
    renderCell: (o) => <CircularBar finishedAt={o.finishedAt} orderId={o.id} />,
  };

  const columns = [
    // Always show title first
    commonColumns[0],
    // Conditionally show "Type"
    ...(shouldIncludeType(orderStatus) ? [typeColumn] : []),
    // The rest of the common columns
    ...commonColumns.slice(1),
    // Conditionally show "Temps restant"
    ...(shouldIncludeTempsRestant(orderStatus) ? [tempsRestantColumn] : []),
  ];

  // 8) Sorting handler
  const handleSortingChange = (field) => {
    console.log(field);
    const direction =
      field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(
      handleSorting({
        table: orderStatus,
        sortField: field,
        sortOrder: direction,
      })
    );
    setSortField(field);
    setSortOrder(direction);
  };

  // 9) Render: loading, error, or main content
  return (
    <div className="content-center">
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorComponent />
      ) : (
        <>
          {/* Toolbar */}
          {toolbar}

          {/* Data Table */}
          <DataTable
            columns={columns}
            data={rows}
            enableSorting
            onRowClick={handleRowClick}
            onSort={handleSortingChange}
            sortField={sortField}
            sortOrder={sortOrder}
          />

          {/* Paginator */}
          <Paginator
            isLoading={isLoading}
            listLength={filteredOrders.length}
            currentPage={currentPage}
            pageNumbers={[...Array(totalPages).keys()].map((i) => i + 1)}
            nbElementsPerPage={ordersPerPage}
            goToPage={setCurrentPage}
          />
        </>
      )}
    </div>
  );
}
